import { useState } from "react";
import Button from "./Button";
import { ArrowRight, Check } from "./icons";
import { COMPANY } from "../data/company";
import {
  DEFAULT_TERM,
  TERMS,
  TIERS,
  capLabel,
  formatDate,
  formatPrice,
  liveSale,
  perDay,
  perMonth,
  salePrice,
  savingsVsShortest,
  termSavingPct,
} from "../data/pricing";
import "./plan-cards.css";

const CONSOLE_URL = `${COMPANY.urls.console}/login`;

/** The next multiple of 10 above the per-day price, so "less than" is always true. */
const dayCeiling = (price, term) => (Math.floor(perDay(price, term) / 10) + 1) * 10;

/**
 * The plan cards on /pricing. The home page shows no price (sections/StartFree.jsx).
 *
 * Term first, then the tiers side by side: the same order the paywall in the
 * console asks the question in. Everything on a card comes from the catalog:
 * name, tagline, caps, bullets, badge, price and any live sale.
 *
 * Each card answers, in order: what it is called, who it is for, what it
 * costs, what that is per month, what you save by committing, what you are
 * allowed (members, staff), and what is in it.
 */
export default function PlanCards({ headingLevel = 3 }) {
  const [term, setTerm] = useState(DEFAULT_TERM);
  const sale = liveSale();
  const Heading = `h${headingLevel}`;

  return (
    <div className="plans">
      {sale && <SaleBanner sale={sale} />}

      <p className="plans__trial">
        <strong>First {COMPANY.trialDays} days free</strong> on every plan. No
        card, and every feature is in the trial except Auto reminders.
      </p>

      {TERMS.length > 1 && (
        <div className="plans__terms" role="group" aria-label="Billing term">
          {TERMS.map((months) => (
            <button
              key={months}
              type="button"
              className="plans__term"
              aria-pressed={months === term}
              onClick={() => setTerm(months)}
            >
              {months} months
              {termSavingPct(months) > 0 && (
                <span className="plans__hint">save up to {termSavingPct(months)}%</span>
              )}
            </button>
          ))}
        </div>
      )}

      <ul className="plans__grid">
        {TIERS.filter((tier) => tier.prices[term]).map((tier) => {
          const list = tier.prices[term];
          const offer = sale ? salePrice(sale, tier, term) : null;
          const price = offer ?? list;
          // Measured against the price on the card, sale included, so the
          // number is what this purchase actually saves.
          const saved = savingsVsShortest({ ...tier, prices: { ...tier.prices, [term]: price } }, term);
          // The cheapest tier carries its per-day cost as a tag (unless the
          // catalog already badged it), so the first number an owner reads is
          // small and true: "less than" the next round figure above the price spread
          // over the days it covers, and said as before GST.
          const dayTag = !tier.badge && tier === TIERS[0] ? `Less than ${formatPrice(dayCeiling(price, term))} a day + GST` : "";

          return (
            <li
              key={tier.tier}
              className={`plans__card${tier.highlight ? " plans__card--highlight" : ""}`}
            >
              {tier.badge && <p className="plans__badge">{tier.badge}</p>}
              {dayTag && <p className="plans__badge plans__badge--soft">{dayTag}</p>}

              <div className="plans__top">
                <Heading className="plans__name">{tier.name}</Heading>
                {tier.tagline && <p className="plans__tagline">{tier.tagline}</p>}
              </div>

              <div className="plans__cost">
                <p className="plans__price">
                  {offer !== null && offer < list && (
                    <s className="plans__was" aria-label={`was ${formatPrice(list)}`}>
                      {formatPrice(list)}
                    </s>
                  )}
                  <span className="plans__amount">{formatPrice(price)}</span>
                </p>
                <p className="plans__permonth">
                  {formatPrice(perMonth(price, term))}/mo for {term} months, + GST
                </p>
                {saved > 0 && (
                  <p className="plans__saving">
                    Save {formatPrice(saved)} against {TERMS[0]}-month terms
                  </p>
                )}
              </div>

              <dl className="plans__caps">
                <div className="plans__cap">
                  <dt>Members</dt>
                  <dd>{capLabel(tier.maxMembers)}</dd>
                </div>
                <div className="plans__cap">
                  <dt>Trainers and staff</dt>
                  <dd>{capLabel(tier.maxStaff)}</dd>
                </div>
              </dl>

              {tier.bullets.length > 0 && (
                <ul className="plans__bullets">
                  {tier.bullets.map((bullet) => (
                    <li key={bullet}>
                      <Check />
                      {bullet}
                    </li>
                  ))}
                </ul>
              )}

              <Button
                href={CONSOLE_URL}
                variant={tier.highlight ? "primary" : "quiet"}
                size="md"
                className="plans__cta"
              >
                Start free
                <span className="btn__glyph">
                  <ArrowRight />
                </span>
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * A running sale, said plainly: what it is, that the prices below already
 * include it, and when it ends. The end date is the reason to act, so it is
 * never hidden behind a countdown.
 */
function SaleBanner({ sale }) {
  const bits = [];
  if (sale.endsAt) bits.push(`Ends ${formatDate(sale.endsAt)}.`);
  if (sale.firstPurchaseOnly) bits.push("For your first payment.");
  if (sale.limited) bits.push("While it lasts.");

  return (
    <p className="plans__sale" role="status">
      <strong>{sale.label}</strong>
      <span>
        The prices below already include it. {bits.join(" ")}
      </span>
    </p>
  );
}
